import { Button } from '@monorepo/shared/componentsV2/Button';
import { RadioGroupField } from '@monorepo/shared/componentsV2/fields/RadioGroupField';
import { Anchor } from '@monorepo/shared/componentsV2/Link';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { RadioGroupOption } from '@monorepo/shared/componentsV2/RadioGroup';
import { siteRolesProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import { bodyMedium } from '@monorepo/shared/styles/text';
import { isRequired as isRequiredValidator } from '@monorepo/shared/utils/validators';
import { ProjectPermissionRoles } from 'mapistry-shared';
import React, { type ReactNode } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';

const EmailSlot = styled.div<{ $withMarginTop?: boolean }>`
  margin-top: ${({ $withMarginTop }) => ($withMarginTop ? '1rem' : 0)};
  margin-bottom: 1rem;
`;

const SiteRoleHeader = styled.div`
  display: flex;
  gap: 1rem;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const SiteRoleLabel = styled.span`
  ${bodyMedium}
  color: ${({ theme }) => theme.colors.gray333};
`;

const RolesHelpLink = styled(Anchor)`
  white-space: nowrap;
`;

export type UserSiteAccessFormValues = {
  role: ProjectPermissionRoles;
};

// Shared shell for inviting a new user to a site and for updating an existing
// user's site role. Callers supply the email control and submit handler
interface UserSiteAccessModalProps<T extends UserSiteAccessFormValues> {
  title: string;
  description?: string;
  emailSlot: ReactNode;
  roleOptions: RadioGroupOption[];
  initialValues: T;
  onSubmit: (values: T) => void | Promise<void>;
  onClose: () => void;
  submitLabel: string;
  submitPendingLabel: string;
  disableWhenPristine?: boolean;
}

export function UserSiteAccessModal<T extends UserSiteAccessFormValues>(
  props: UserSiteAccessModalProps<T>,
) {
  const {
    title,
    description,
    emailSlot,
    roleOptions,
    initialValues,
    onSubmit,
    onClose,
    submitLabel,
    submitPendingLabel,
    disableWhenPristine = false,
  } = props;

  return (
    <Form<T>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{
        submitting: true,
        pristine: true,
        hasValidationErrors: true,
      }}
    >
      {({ handleSubmit, submitting, pristine, hasValidationErrors }) => (
        <BaseModal
          open
          onClose={submitting ? undefined : onClose}
          showCloseButton
          title={title}
          description={description}
          maxWidth="sm"
          buttons={[
            <Button
              key="cancel"
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                hasValidationErrors ||
                (disableWhenPristine && pristine)
              }
            >
              {submitting ? submitPendingLabel : submitLabel}
            </Button>,
          ]}
        >
          <form onSubmit={handleSubmit}>
            <EmailSlot $withMarginTop={Boolean(description)}>
              {emailSlot}
            </EmailSlot>
            <SiteRoleHeader>
              <SiteRoleLabel>Site Role</SiteRoleLabel>
              <RolesHelpLink
                href={siteRolesProductGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about site roles
              </RolesHelpLink>
            </SiteRoleHeader>
            <RadioGroupField
              isLabelHidden
              label="Site Role"
              name="role"
              options={roleOptions}
              required
              validate={isRequiredValidator}
              variant="outlined"
              forceShowErrors={false}
              isReadOnly={submitting}
            />
          </form>
        </BaseModal>
      )}
    </Form>
  );
}
